import { useEffect } from "react";
import { Form, Offcanvas, Button, InputGroup, Row, Col } from "react-bootstrap";
import { CiChat1, CiChat2 } from "react-icons/ci";
import { FiPlus } from "react-icons/fi";
import { FaRocketchat } from "react-icons/fa6";
import {
  IoIosChatboxes,
  IoIosChatbubbles,
  IoMdChatboxes,
  IoMdChatbubbles,
} from "react-icons/io";
import {
  IoChatbox,
  IoChatboxEllipses,
  IoChatboxEllipsesOutline,
  IoChatboxEllipsesSharp,
} from "react-icons/io5";
import {
  BsChatText,
  BsFillChatRightTextFill,
  BsChatLeftDots,
  BsChatSquareText,
  BsChatHeartFill,
} from "react-icons/bs";
import { RiChatSmileAiFill, RiRobot3Line } from "react-icons/ri";
import { MdOutlineSupportAgent, MdOutlineMarkChatUnread } from "react-icons/md";
import { BiSupport } from "react-icons/bi";
import { SlUser, SlUserFemale } from "react-icons/sl";
import { PiChatsBold } from "react-icons/pi";
import { GrChatOption } from "react-icons/gr";
import { HiOutlineChatBubbleLeftRight } from "react-icons/hi2";
import FileManagementModal from "../PageSettings/FileManagementModal";
import UseToggle from "../../../Hooks/UseToggle";

const ICONS = {
  "CiChat1": CiChat1,
  "CiChat2": CiChat2,
  "FaRocketchat": FaRocketchat,
  "IoIosChatboxes": IoIosChatboxes,
  "IoIosChatbubbles": IoIosChatbubbles,
  "IoMdChatboxes": IoMdChatboxes,
  "IoMdChatbubbles": IoMdChatbubbles,
  "IoChatbox": IoChatbox,
  "IoChatboxEllipses": IoChatboxEllipses,
  "IoChatboxEllipsesOutline": IoChatboxEllipsesOutline,
  "IoChatboxEllipsesSharp": IoChatboxEllipsesSharp,
  "BsChatText": BsChatText,
  "BsFillChatRightTextFill": BsFillChatRightTextFill,
  "RiChatSmileAiFill": RiChatSmileAiFill,
  "HiOutlineChatBubbleLeftRight": HiOutlineChatBubbleLeftRight,
  "PiChatsBold": PiChatsBold,
  "GrChatOption": GrChatOption,
  "BsChatLeftDots": BsChatLeftDots,
  "BsChatSquareText": BsChatSquareText,
  "RiRobot3Line": RiRobot3Line,
  "MdOutlineSupportAgent": MdOutlineSupportAgent,
  "BiSupport": BiSupport,
  "SlUser": SlUser,
  "SlUserFemale": SlUserFemale,
  "BsChatHeartFill": BsChatHeartFill,
  "MdOutlineMarkChatUnread": MdOutlineMarkChatUnread,
};

const ICONS_ARRAY = Object.entries(ICONS).map(([key, value]) => ({
  name: key,
  icon: value,
}));

const AdvanceWidgetAppearance = ({
  show,
  hide,
  title,
  colors,
  updateWidgetColorSettings,
  setWidgetColorSettings,
  widgetColorSettings,
  initialColorSettings,
  hasDifferentValue
}) => {
  const toggles = UseToggle();

  const previewStyle = {
    width: widgetColorSettings?.width ? `${widgetColorSettings?.width}px` : 60,
    height: widgetColorSettings?.height ? `${widgetColorSettings?.height}px` : 60,
    borderRadius: widgetColorSettings?.shape === "rounded" ? "50%" : "8px",
    background: widgetColorSettings?.color,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  useEffect(() => {
    if (widgetColorSettings?.shape === "rounded") {
      setWidgetColorSettings((prev) => ({ ...prev, text: "", width: 50, height: 50, iconSize: 30, textSize: 0 }));
    } else {
      setWidgetColorSettings((prev) => ({ ...prev, text: "Help us", width: 100, height: 50, iconSize: 25, textSize: 18 }));
    }
  }, [widgetColorSettings?.shape]);

  let SettingIcon = widgetColorSettings.icon === "image" ? "" : ICONS[widgetColorSettings.icon]

  return (
    <Offcanvas show={show} onHide={hide} placement="end">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{title}</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <div className="bg-light rounded p-2 border">
          <div style={{ height: 120 }} className={`d-flex align-items-end ${widgetColorSettings?.position === "right" ? "justify-content-end" : "justify-content-start"}`}>
            <div style={previewStyle}>
              {widgetColorSettings?.icon === "image" ?
                <img
                  src={widgetColorSettings?.imageLink}
                  alt={"Custom Image"}
                  style={{
                    width: widgetColorSettings?.iconSize,
                    height: widgetColorSettings?.iconSize,
                    borderRadius: "100%"
                  }}
                />
                : <SettingIcon size={Number(widgetColorSettings?.iconSize)} />
              }
              {widgetColorSettings?.shape === "square" && widgetColorSettings?.text && (
                <span style={{ fontSize: `${Number(widgetColorSettings?.textSize)}px` }}>{widgetColorSettings?.text}</span>
              )}
            </div>
          </div>
        </div>

        <Form.Group className="py-3">
          <Form.Group className="hstack flex-wrap gap-1 mb-2">
            {colors.map((color, index) => (
              <div
                key={index}
                onClick={(e) => updateWidgetColorSettings("color", color)}
                style={{
                  width: 25,
                  height: 25,
                  backgroundColor: color,
                  borderRadius: 4,
                  cursor: "pointer",
                  border:
                    widgetColorSettings?.color === color
                      ? "2px solid #000"
                      : "1px solid #ccc",
                }}
                title={color}
              />
            ))}
          </Form.Group>
          <Row>
            <Col>
              <Form.Label>Width</Form.Label>
              <InputGroup>
                <Form.Control
                  size="sm"
                  type="number"
                  value={widgetColorSettings?.width}
                  onChange={(e) => updateWidgetColorSettings("width", e.target.value)}
                  placeholder="Enter width"
                />
                <InputGroup.Text style={{ fontSize: 10 }}>PX</InputGroup.Text>
              </InputGroup>
            </Col>
            <Col>
              <Form.Label>Height</Form.Label>
              <InputGroup>
                <Form.Control
                  size="sm"
                  type="number"
                  value={widgetColorSettings?.height}
                  onChange={(e) => updateWidgetColorSettings("height", e.target.value)}
                  placeholder="Enter height"
                />
                <InputGroup.Text style={{ fontSize: 10 }}>PX</InputGroup.Text>
              </InputGroup>
            </Col>
          </Row>
        </Form.Group>
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Floating Position</Form.Label>
              {["left", "right"].map((pos) => (
                <Form.Check
                  key={pos}
                  type="radio"
                  style={{ fontSize: 14 }}
                  label={pos === "left" ? "Left" : "Right"}
                  checked={widgetColorSettings?.position === pos}
                  onChange={() => updateWidgetColorSettings("position", pos)}
                />
              ))}
            </Form.Group>
          </Col>

          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Icon Shape</Form.Label>
              {["rounded", "square"].map((shape) => (
                <Form.Check
                  key={shape}
                  type="radio"
                  style={{ fontSize: 14 }}
                  label={shape === "rounded" ? "Rounded" : "Square"}
                  checked={widgetColorSettings?.shape === shape}
                  onChange={() => { updateWidgetColorSettings("shape", shape); updateWidgetColorSettings("width", widgetColorSettings?.height * 2) }}
                />
              ))}
            </Form.Group>
          </Col>
        </Row>
        {widgetColorSettings?.shape === "square" && (
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Widget Text</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={widgetColorSettings?.text}
                  onChange={(e) => updateWidgetColorSettings("text", e.target.value)}
                  placeholder="Enter widget text"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Widget Text Size</Form.Label>
                <InputGroup>
                  <Form.Control
                    size="sm"
                    type="number"
                    value={widgetColorSettings?.textSize}
                    onChange={(e) => updateWidgetColorSettings("textSize", e.target.value)}
                    placeholder="Enter Widget Text Size"
                  />
                  <InputGroup.Text style={{ fontSize: 10 }}>PX</InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Icon Size</Form.Label>
          <InputGroup>
            <Form.Control
              size="sm"
              type="number"
              value={widgetColorSettings?.iconSize}
              onChange={(e) => updateWidgetColorSettings("iconSize", e.target.value)}
              placeholder="Enter Icon Size"
            />
            <InputGroup.Text style={{ fontSize: 10 }}>PX</InputGroup.Text>
          </InputGroup>
        </Form.Group>

        <Form.Group>
          <Form.Label>Select Icon</Form.Label>
          <div
            className="p-2 mt-2 rounded bg-light d-flex flex-wrap gap-2"
            style={{ border: "1px dashed var(--border-color)" }}
          >
            <Button
              variant={widgetColorSettings?.icon === "image" ? "primary" : "light"}
              className="border"
              onClick={() => {
                updateWidgetColorSettings("icon", "image");
                toggles.setToggle()
              }}
            >
              <FiPlus />
            </Button>
            {ICONS_ARRAY.map((Item, i) => {
              const Icon = Item.icon;
              return (
                <Button
                  key={i}
                  variant={widgetColorSettings?.icon === Item.name ? "primary" : "light"}
                  className="border"
                  onClick={() => {
                    updateWidgetColorSettings("icon", Item.name);
                    updateWidgetColorSettings("imageLink", "")
                  }}
                >
                  <Icon />
                </Button>
              );
            })}
          </div>
        </Form.Group>
        {hasDifferentValue() &&
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              size="sm"
              onClick={() => setWidgetColorSettings(initialColorSettings)}
              variant="outline-danger"
            >
              Reset
            </Button>
          </div>
        }
        <FileManagementModal
          show={!toggles.toggle}
          hide={toggles.setToggle}
        />
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AdvanceWidgetAppearance;
